import React, { useState, useEffect } from "react";
import { Form, Button, Input, Radio, message, Select } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import "./AssessmentStyles.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTerminology } from "../hooks/useTerminology";
import {
  apiService,
  Assessment,
  ControlResponse,
  MonitoringResponse,
  TeamMember,
} from "../services/apiService";
import {
  CMDA_CONTROL_QUESTIONS,
  CMDA_MONITORING_QUESTIONS,
  CMA_ASSESSMENT_QUESTIONS,
  ASSESSMENT_METADATA,
} from "../data/questionnaire-data";
import { adaptQuestion } from "../utils/adaptQuestions";
import {
  analyzeControlDescription,
  mapObligations,
  prefillCMDAAnswers,
  prefillCMAQuestions,
  suggestCMARecommendations,
  generateRationale,
  AI5WHAnalysis,
  AIObligationMapping,
  AIPrefillResponse,
  AICMAQuestionPrefill,
  AIRecommendation,
} from "../services/aiService";
import {
  AITriggerBar,
  AIAnalysisResult,
  AIPanel,
  AIProcessing,
  AIInlineConfidence,
  AIRecommendationPanel,
  AIOverrideNotice,
} from "./AIAssistant";

const { TextArea } = Input;

// Questionnaire data is now imported from questionnaire-data.ts

const LOW_INFO_REVIEW_RE =
  /^(?:(?:i\s*)?(?:don'?t|do\s*not)\s*know|n\/?a|none|nothing|idk|\?+|tbd|tba|unknown|no\s*idea)\.?\s*$/i;

function isReviewAnalyzable(text: string): boolean {
  const t = (text || "").trim();
  if (t.length < 30) return false;
  if (LOW_INFO_REVIEW_RE.test(t)) return false;
  const wordCount = t.split(/\s+/).filter(Boolean).length;
  if (wordCount < 6) return false;
  return true;
}

const AssessmentForm: React.FC = () => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savedAssessmentId, setSavedAssessmentId] = useState<string | null>(
    null,
  );
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const navigate = useNavigate();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const t = useTerminology();
  const isEdit = !!assessmentId;
  const initialType = (searchParams.get("type") as "CMA" | "CMDA") || "CMDA";
  const typePreselected = !!searchParams.get("type") || !!assessmentId;
  const [currentType, setCurrentType] = useState<"CMDA" | "CMA">(initialType);
  const watchedCmaType = Form.useWatch("cmaType", form);

  // ── AI Assistant state ────────────────────────────────────────────────
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<AI5WHAnalysis | null>(null);
  const [aiObligations, setAiObligations] =
    useState<AIObligationMapping | null>(null);
  const [aiPrefills, setAiPrefills] = useState<
    Record<string, AIPrefillResponse>
  >({});
  const [aiOverrides, setAiOverrides] = useState<Record<string, boolean>>({});
  const [cmaActivated, setCmaActivated] = useState(false);
  const [cmaActivating, setCmaActivating] = useState(false);
  const [analyzingProcessProposed, setAnalyzingProcessProposed] =
    useState(false);
  const [analyzingRiskProposed, setAnalyzingRiskProposed] = useState(false);
  const [cmaRecommendations, setCmaRecommendations] = useState<{
    control: AIRecommendation[];
    monitoring: AIRecommendation[];
  }>({ control: [], monitoring: [] });
  const [recState, setRecState] = useState<
    Record<string, { used?: boolean; dismissed?: boolean }>
  >({});
  const [aiRationale, setAiRationale] = useState<{
    rationale: string;
    confidence: number;
  } | null>(null);
  const [failedCmdas, setFailedCmdas] = useState<Assessment[]>([]);
  const [selectedCmda, setSelectedCmda] = useState<Assessment | null>(null);
  const [loadingCmdas, setLoadingCmdas] = useState(false);
  // Loading the existing assessment for edit mode
  const [pageLoading, setPageLoading] = useState<boolean>(isEdit);
  // Save / Next button busy states
  const [saving, setSaving] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  // CMA per-question AI prefill state (control + monitoring)
  const [cmaQuestionPrefills, setCmaQuestionPrefills] = useState<
    Record<string, AICMAQuestionPrefill>
  >({});
  const [cmaPrefillLoading, setCmaPrefillLoading] = useState<{
    control: boolean;
    monitoring: boolean;
  }>({ control: false, monitoring: false });
  // Tracks which CMA questions have been marked N/A (max 1 per section)
  const [cmaNA, setCmaNA] = useState<Record<string, boolean>>({});
  // Tracks which question (if any) has N/A selected in each CMDA section (max 1 per section)
  const [controlNAQid, setControlNAQid] = useState<string | null>(null);
  const [monitoringNAQid, setMonitoringNAQid] = useState<string | null>(null);

  useEffect(() => {
    // Load tenant users for team member dropdown
    loadTenantUsers();

    if (isEdit && assessmentId) {
      loadAssessment();
    } else if (!isEdit) {
      initializeForm();
    }
  }, [isEdit, assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load failed CMDAs for both CMA and CMDA (for optional linking/prefill)
  useEffect(() => {
    setLoadingCmdas(true);
    apiService
      .getAssessments()
      .then((assessments) => {
        const inadequate = assessments.filter(
          (a) =>
            a.assessmentType === "CMDA" &&
            a.status === "submitted" &&
            (a.overallScore === "Inadequate" ||
              a.controlDesignRating === "Inadequate" ||
              a.monitoringDesignRating === "Inadequate"),
        );
        setFailedCmdas(inadequate);
      })
      .catch((err) => {
        console.error("Failed to load CMDAs:", err);
      })
      .finally(() => setLoadingCmdas(false));
  }, [isEdit]);

  // Auto-trigger AI pre-fill when entering Control Design Adequacy (step 2)
  // or Monitoring Effectiveness (step 4), even if user didn't click
  // "Analyse with AI". Control questions use controlDescription,
  // Monitoring questions use monitoringToolDescription.
  useEffect(() => {
    if (currentType !== "CMDA") return;

    const controlQs = CMDA_CONTROL_QUESTIONS.questions.map((q) => ({
      id: q.id,
      question: q.question,
      guidance: q.guidance,
    }));
    const monitorQs = CMDA_MONITORING_QUESTIONS.questions.map((q) => ({
      id: q.id,
      question: q.question,
      guidance: q.guidance,
    }));

    const hasControlPrefills = controlQs.some((q) => aiPrefills[q.id]);
    const hasMonitorPrefills = monitorQs.some((q) => aiPrefills[q.id]);

    const applyPrefills = (field: string) => {
      const cur = form.getFieldValue(field) || [];
      const updated = cur.map((r: any) => {
        const p = aiPrefills[r.questionId];
        if (p && !r.response) {
          return {
            ...r,
            response: p.response,
            comments: p.rationale || r.comments || "",
          };
        }
        return r;
      });
      form.setFieldsValue({ [field]: updated });
    };

    // Step 2: Control Design Adequacy — uses Control Description
    if (currentStep === 2) {
      if (hasControlPrefills) {
        applyPrefills("controlResponses");
      } else {
        const desc = (form.getFieldValue("controlDescription") || "").trim();
        if (!desc) return;
        setAiAnalyzing(true);
        prefillCMDAAnswers(controlQs, desc)
          .then((ctlPref) => {
            const map: Record<string, AIPrefillResponse> = { ...aiPrefills };
            ctlPref.forEach((p) => {
              map[p.questionId] = p;
            });
            setAiPrefills(map);
          })
          .catch((err) => {
            console.error("Auto pre-fill failed", err);
            message.error("AI pre-fill failed");
          })
          .finally(() => setAiAnalyzing(false));
      }
    }

    // Step 4: Monitoring Effectiveness — uses Monitoring Tool Description
    if (currentStep === 4) {
      if (hasMonitorPrefills) {
        applyPrefills("monitoringResponses");
      } else {
        const desc = (
          form.getFieldValue("monitoringToolDescription") || ""
        ).trim();
        if (!desc) return;
        setAiAnalyzing(true);
        prefillCMDAAnswers(monitorQs, desc)
          .then((monPref) => {
            const map: Record<string, AIPrefillResponse> = { ...aiPrefills };
            monPref.forEach((p) => {
              map[p.questionId] = p;
            });
            setAiPrefills(map);
          })
          .catch((err) => {
            console.error("Auto pre-fill failed", err);
            message.error("AI pre-fill failed");
          })
          .finally(() => setAiAnalyzing(false));
      }
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // CMA: when entering Control Assessment or Monitoring Assessment step,
  // prefill each question's Initial Review from controlDescription /
  // monitoringToolDescription. AI must NOT make assumptions: when evidence is
  // missing the backend returns a "Unable to determine" placeholder.
  useEffect(() => {
    if (currentType !== "CMA") return;

    const stepKey = (steps[currentStep] as any)?.key;
    if (stepKey !== "control" && stepKey !== "monitoring") return;

    const isControl = stepKey === "control";
    const description = (
      form.getFieldValue(
        isControl ? "controlDescription" : "monitoringToolDescription",
      ) || ""
    ).trim();
    if (!description) return;

    const questions = isControl
      ? CMA_ASSESSMENT_QUESTIONS.control
      : CMA_ASSESSMENT_QUESTIONS.monitoring;

    // Skip if all questions already have prefills cached for this run
    const allCached = questions.every((q) => cmaQuestionPrefills[q.id]);
    if (allCached) return;

    // Skip if user already has non-empty initialReview values for every question
    const fieldName = isControl ? "controlResponses" : "monitoringResponses";
    const currentList: any[] = form.getFieldValue(fieldName) || [];
    const allFilled =
      currentList.length === questions.length &&
      currentList.every(
        (r) =>
          typeof r?.initialReview === "string" &&
          r.initialReview.trim().length > 0 &&
          r.initialReview !== r.question, // not just the placeholder
      );
    if (allFilled) return;

    setCmaPrefillLoading((prev) => ({
      ...prev,
      [isControl ? "control" : "monitoring"]: true,
    }));
    prefillCMAQuestions(
      questions.map((q) => ({
        id: q.id,
        question: q.question,
        guidance: q.guidance,
      })),
      description,
    )
      .then((results) => {
        const map: Record<string, AICMAQuestionPrefill> = {
          ...cmaQuestionPrefills,
        };
        results.forEach((r) => {
          map[r.questionId] = r;
        });
        setCmaQuestionPrefills(map);

        const list: any[] = form.getFieldValue(fieldName) || [];
        const updated = list.map((row) => {
          const p = map[row.questionId];
          if (!p) return row;
          // Only fill if user hasn't already provided their own text
          const userHasText =
            typeof row.initialReview === "string" &&
            row.initialReview.trim().length > 0 &&
            row.initialReview !== row.question;
          if (userHasText) return row;
          const proposedFill =
            !p.sufficient && p.proposedChange && p.proposedChange.trim()
              ? p.proposedChange
              : row.proposedChanges;
          return {
            ...row,
            initialReview: p.assessment,
            proposedChanges: proposedFill,
          };
        });
        form.setFieldsValue({ [fieldName]: updated });
      })
      .catch((err) => {
        console.error("CMA prefill failed", err);
      })
      .finally(() => {
        setCmaPrefillLoading((prev) => ({
          ...prev,
          [isControl ? "control" : "monitoring"]: false,
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentType]);

  // Re-apply when aiPrefills state updates (from auto or manual trigger)
  useEffect(() => {
    if (currentType !== "CMDA" || Object.keys(aiPrefills).length === 0) return;
    if (currentStep === 2) {
      const cur = form.getFieldValue("controlResponses") || [];
      const updated = cur.map((r: any) => {
        const p = aiPrefills[r.questionId];
        if (p && !r.response) {
          return {
            ...r,
            response: p.response,
            comments: p.rationale || r.comments || "",
          };
        }
        return r;
      });
      form.setFieldsValue({ controlResponses: updated });
    }
    if (currentStep === 4) {
      const cur = form.getFieldValue("monitoringResponses") || [];
      const updated = cur.map((r: any) => {
        const p = aiPrefills[r.questionId];
        if (p && !r.response) {
          return {
            ...r,
            response: p.response,
            comments: p.rationale || r.comments || "",
          };
        }
        return r;
      });
      form.setFieldsValue({ monitoringResponses: updated });
    }
  }, [aiPrefills]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTenantUsers = async () => {
    try {
      const users = await apiService.getUsers();
      // Filter out current user from team member list
      // User from AuthContext has username (Cognito sub), User from API has userId
      // userId in DynamoDB should be the Cognito sub (for consistency)
      const currentUserId = user?.username; // This is the Cognito sub
      const currentUserEmail = user?.email; // Also check by email as fallback
      const filteredUsers = (users || []).filter((u: any) => {
        const uId = u.userId; // This should be the Cognito sub
        const uEmail = u.email;
        // Match by userId (Cognito sub) or email (for backward compatibility)
        const matches = uId === currentUserId || uEmail === currentUserEmail;
        return !matches;
      });
      setTenantUsers(filteredUsers);
    } catch (error) {
      console.error("Error loading tenant users:", error);
    }
  };

  const loadAssessment = async () => {
    if (!assessmentId) return;

    try {
      setLoading(true);
      setPageLoading(true);
      const data = await apiService.getAssessment(assessmentId);

      if (data.status === "submitted") {
        message.error("Submitted assessments cannot be edited.");
        navigate(`/assessments/${assessmentId}`);
        return;
      }

      setAssessment(data);

      // Update current type to match loaded assessment FIRST
      const assessmentType = data.assessmentType || "CMDA";
      setCurrentType(assessmentType);

      // Wait for state to update, then set form values
      // Use setTimeout to ensure currentType state is updated
      setTimeout(() => {
        // Get the correct questionnaire structure based on type
        let controlQuestions: any[] = [];
        let monitoringQuestions: any[] = [];

        if (assessmentType === "CMDA") {
          controlQuestions = CMDA_CONTROL_QUESTIONS.questions;
          monitoringQuestions = CMDA_MONITORING_QUESTIONS.questions;
        } else if (assessmentType === "CMA") {
          controlQuestions = CMA_ASSESSMENT_QUESTIONS.control;
          monitoringQuestions = CMA_ASSESSMENT_QUESTIONS.monitoring;
        }

        // Merge loaded responses with question structure
        const controlResponses = controlQuestions.map((q) => {
          const existing = (data.controlResponses || []).find(
            (r: any) => r.questionId === q.id,
          );
          if (assessmentType === "CMA") {
            if (existing) {
              return {
                ...existing,
                initialReview:
                  existing.initialReview &&
                  existing.initialReview.trim().length > 0
                    ? existing.initialReview
                    : "",
              };
            }
            return {
              questionId: q.id,
              question: q.question,
              category: q.category,
              guidance: q.guidance,
              response: "",
              initialReview: "",
              proposedChanges: "",
            };
          } else {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              }
            );
          }
        });

        const monitoringResponses = monitoringQuestions.map((q) => {
          const existing = (data.monitoringResponses || []).find(
            (r: any) => r.questionId === q.id,
          );
          if (assessmentType === "CMA") {
            if (existing) {
              return {
                ...existing,
                initialReview:
                  existing.initialReview &&
                  existing.initialReview.trim().length > 0
                    ? existing.initialReview
                    : "",
              };
            }
            return {
              questionId: q.id,
              question: q.question,
              category: q.category,
              guidance: q.guidance,
              response: null,
              comments: "",
            };
          } else {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              }
            );
          }
        });

        const normalizedTeamMembers =
          typeof data.teamMembers === "string"
            ? data.teamMembers
            : Array.isArray((data as any).teamMembers)
              ? (data as any).teamMembers
                  .map((tm: any) =>
                    typeof tm === "string" ? tm : tm.userId || tm,
                  )
                  .join(", ")
              : "";

        const normalizedRiskIdsField = Array.isArray(data.riskIds)
          ? data.riskIds.join(", ")
          : typeof data.riskIds === "string"
            ? data.riskIds
            : "";

        form.setFieldsValue({
          assessmentName: data.assessmentName,
          assessmentType: assessmentType,
          controlResponses,
          monitoringResponses,
          teamMembers: normalizedTeamMembers,
          finalAssessmentComment: data.finalAssessmentComment || "",
          // CMA fields
          processId: data.processId || "",
          processDescription: data.processDescription || "",
          activityDescription: data.activityDescription || "",
          riskIds: normalizedRiskIdsField,
          riskConsequences: data.riskConsequences || "",
          processInitialReview: data.processInitialReview || "",
          processProposedChanges: data.processProposedChanges || "",
          riskInitialReview: data.riskInitialReview || "",
          riskProposedChanges: data.riskProposedChanges || "",
          // CMDA CDA fields
          controlDesignRating: data.controlDesignRating,
          controlIssueId: data.controlIssueId || "",
          designCompletionDate: data.designCompletionDate || "",
          facilitatorName: data.facilitatorName || "",
          facilitatorEmployeeId: data.facilitatorEmployeeId || "",
          facilitatorKnowledgeable: data.facilitatorKnowledgeable,
          designParticipants: data.designParticipants || "",
          controlPerformerRepresentative:
            data.controlPerformerRepresentative || "",
          controlId: data.controlId || "",
          controlDescription: data.controlDescription || "",
          regulationObligationIds: data.regulationObligationIds || "",
          // CMDA MDA fields
          monitoringDesignRating: data.monitoringDesignRating,
          monitoringIssueId: data.monitoringIssueId || "",
          monitoringToolId: data.monitoringToolId || "",
          monitoringToolDescription: data.monitoringToolDescription || "",
          // CMA linking fields
          linkedCmdaId: data.linkedCmdaId || "",
          cmaControlIds: data.cmaControlIds || "",
          cmaMonitoringIds: data.cmaMonitoringIds || "",
          cmaType: data.cmaType || "remediation",
        });

        // Load linked CMDA data if it exists
        if (data.linkedCmdaId) {
          apiService
            .getAssessment(data.linkedCmdaId)
            .then((cmda) => setSelectedCmda(cmda))
            .catch((err) => console.error("Failed to load linked CMDA:", err));
        }

        // Force form to re-render
        form.validateFields().catch(() => {
          // Ignore validation errors when loading
        });
        setPageLoading(false);
      }, 100);
    } catch (error) {
      console.error("Error loading assessment:", error);
      message.error("No Assessment have been created");
      navigate("/assessments");
      setPageLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const initializeForm = () => {
    // Get questionnaire data based on assessment type
    let controlQuestions: any[] = [];
    let monitoringQuestions: any[] = [];

    if (currentType === "CMDA") {
      controlQuestions = CMDA_CONTROL_QUESTIONS.questions;
      monitoringQuestions = CMDA_MONITORING_QUESTIONS.questions;
    } else if (currentType === "CMA") {
      controlQuestions = CMA_ASSESSMENT_QUESTIONS.control;
      monitoringQuestions = CMA_ASSESSMENT_QUESTIONS.monitoring;
    }

    form.setFieldsValue({
      assessmentName: `New ${currentType} Assessment`,
      assessmentType: currentType,
      teamMembers: "",
      finalAssessmentComment: "",
      cmaType: currentType === "CMA" ? "remediation" : "",
      // CMA fields
      processId: "",
      processDescription: "",
      activityDescription: "",
      riskIds: "",
      riskConsequences: "",
      processInitialReview: "",
      processProposedChanges: "",
      riskInitialReview: "",
      riskProposedChanges: "",
      // CMDA CDA fields
      controlDesignRating: undefined,
      controlIssueId: "",
      designCompletionDate: "",
      facilitatorName: "",
      facilitatorEmployeeId: "",
      facilitatorKnowledgeable: undefined,
      designParticipants: "",
      controlPerformerRepresentative: "",
      controlId: "",
      controlDescription:
        currentType === "CMA"
          ? ""
          : "On a weekly basis, after the US_Cancelled_Case_Report has been uploaded to the Control MIS system, the Operations Dispute agent reviews the report for cancelled cases. The agent will use the Case ID to access the account in Oracle to determine the exact date/time when the case was submitted. The agent will then locate the call in Cisco using the date, time and agent ID and listen to the call interaction where the case was cancelled and update Zendesk to indicate of remediation is required for the following Agent cancelled case and case was opened a subsequent day, Agent cancelled case and no new case opened, No call located and no case opened same day. The agent will review Oracle to confirm if any other cases were opened same day (confirm case contains same transaction from the cancelled case). If remediation is required, submit an Misc Case and include the required note and information. If remediation is not required place the applicable review note on the account. This Control is executed across NAM - US - Contact Center Operations and on behalf of NAM - US - Customer Service - Retail to ensure remediation for cancelled cases, if applicable, is completed accurately, correctly and timely and to remain compliant with Regulation E (implementing the Electronic Funds Transfer Act) 12 CFR Part 1005, Obligations: 1441005.34 Procedures for Cancellation and Refund of Remittance Transfers; 144353: Remittance Transfers - Accuracy",
      regulationObligationIds:
        "122608 - Regulation E (implementing the Electronic Funds Transfer Act) 12 CFR Part 1005\n1441005 - Remittance Transfers: Disclosure Requirements - Long form error resolution and cancellation notice",
      // CMDA MDA fields
      monitoringDesignRating: undefined,
      monitoringIssueId: "",
      monitoringToolId: "",
      monitoringToolDescription: "",
      controlResponses: controlQuestions.map((q) => {
        if (currentType === "CMA") {
          return {
            questionId: q.id,
            question: q.question,
            category: q.category,
            guidance: q.guidance,
            response: "",
            initialReview: "",
            proposedChanges: "",
          };
        } else {
          return {
            questionId: q.id,
            question: q.question,
            category: q.category,
            guidance: q.guidance,
            response: null,
            comments: "",
          };
        }
      }),
      monitoringResponses: monitoringQuestions.map((q) => {
        if (currentType === "CMA") {
          return {
            questionId: q.id,
            question: q.question,
            category: q.category,
            guidance: q.guidance,
            response: "",
            initialReview: "",
            proposedChanges: "",
          };
        } else {
          return {
            questionId: q.id,
            question: q.question,
            category: q.category,
            guidance: q.guidance,
            response: null,
            comments: "",
          };
        }
      }),
    });
  };

  // React to assessment type changes from the form
  const handleValuesChange = (changed: any, all: any) => {
    if (Object.prototype.hasOwnProperty.call(changed, "assessmentType")) {
      const newType = changed.assessmentType as "CMDA" | "CMA";
      console.log("Assessment type changed to:", newType, "isEdit:", isEdit);
      setCurrentType(newType);
      // Reset to first step when type changes to avoid step mismatch
      setCurrentStep(0);

      // Regenerate questions when type changes (for both new and edit)
      // This ensures questions match the selected type
      let controlQuestions: any[] = [];
      let monitoringQuestions: any[] = [];
      if (newType === "CMDA") {
        controlQuestions = CMDA_CONTROL_QUESTIONS.questions;
        monitoringQuestions = CMDA_MONITORING_QUESTIONS.questions;
      } else {
        controlQuestions = CMA_ASSESSMENT_QUESTIONS.control;
        monitoringQuestions = CMA_ASSESSMENT_QUESTIONS.monitoring;
      }

      // For edit mode, preserve existing responses if they match the new type
      // For new assessments, initialize with empty responses
      if (isEdit) {
        // Get current form values
        const currentValues = form.getFieldsValue(true);
        const existingControl = currentValues.controlResponses || [];
        const existingMonitoring = currentValues.monitoringResponses || [];

        // Merge with new question structure
        const mergedControl = controlQuestions.map((q) => {
          const existing = existingControl.find(
            (r: any) => r.questionId === q.id,
          );
          if (newType === "CMA") {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: "",
                initialReview: "",
                proposedChanges: "",
              }
            );
          } else {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              }
            );
          }
        });

        const mergedMonitoring = monitoringQuestions.map((q) => {
          const existing = existingMonitoring.find(
            (r: any) => r.questionId === q.id,
          );
          if (newType === "CMA") {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: "",
                initialReview: "",
                proposedChanges: "",
              }
            );
          } else {
            return (
              existing || {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              }
            );
          }
        });

        form.setFieldsValue({
          assessmentType: newType,
          controlResponses: mergedControl,
          monitoringResponses: mergedMonitoring,
        });
      } else {
        // New assessment - initialize with empty responses
        form.setFieldsValue({
          assessmentType: newType,
          // Clear CMDA demo data when switching to CMA
          ...(newType === "CMA" ? { controlDescription: "" } : {}),
          controlResponses: controlQuestions.map((q) => {
            if (newType === "CMA") {
              return {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: "",
                initialReview: "",
                proposedChanges: "",
              };
            } else {
              return {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              };
            }
          }),
          monitoringResponses: monitoringQuestions.map((q) => {
            if (newType === "CMA") {
              return {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: "",
                initialReview: "",
                proposedChanges: "",
              };
            } else {
              return {
                questionId: q.id,
                question: q.question,
                category: q.category,
                guidance: q.guidance,
                response: null,
                comments: "",
              };
            }
          }),
        });
      }
    }
  };

  // ── AI handlers ──────────────────────────────────────────────────────
  const runCDAAnalysis = async () => {
    const description = form.getFieldValue("controlDescription") || "";
    if (!description.trim()) {
      message.warning("Enter a control description first");
      return;
    }
    setAiAnalyzing(true);
    try {
      const [analysis, obligations] = await Promise.all([
        analyzeControlDescription(description),
        mapObligations(description),
      ]);
      setAiAnalysis(analysis);
      setAiObligations(obligations);

      // Pre-fill only CMDA control questions from Control Description
      const controlQs = CMDA_CONTROL_QUESTIONS.questions.map((q) => ({
        id: q.id,
        question: q.question,
        guidance: q.guidance,
      }));
      const ctlPref = await prefillCMDAAnswers(controlQs, description);
      const prefillMap: Record<string, AIPrefillResponse> = { ...aiPrefills };
      ctlPref.forEach((p) => {
        prefillMap[p.questionId] = p;
      });
      setAiPrefills(prefillMap);

      // Apply control pre-fills to form (including AI comments)
      const currentControl = form.getFieldValue("controlResponses") || [];
      const updatedControl = currentControl.map((r: ControlResponse) => {
        const p = prefillMap[r.questionId];
        if (p && !r.response) {
          return {
            ...r,
            response: p.response,
            comments: p.rationale || r.comments || "",
          };
        }
        return r;
      });
      form.setFieldsValue({ controlResponses: updatedControl });
      setAiOverrides({});
      message.success("AI analysis complete — control answers pre-filled");
    } catch (e) {
      message.error("AI analysis failed");
      console.error(e);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const activateCMAAI = async () => {
    const processDesc = form.getFieldValue("processInitialReview") || "";
    const riskConsequences = form.getFieldValue("riskInitialReview") || "";
    const controlIds = form.getFieldValue("cmaControlIds") || "";
    const monitoringIds = form.getFieldValue("cmaMonitoringIds") || "";
    const activityDesc = form.getFieldValue("activityDescription") || "";

    // Build enriched context
    let context = `Control ID(s): ${controlIds}\nMonitoring ID(s): ${monitoringIds}\nActivity: ${activityDesc}\nProcess Initial Review: ${processDesc}\nRisk Initial Review: ${riskConsequences}`;

    // Add linked CMDA context if available
    if (selectedCmda) {
      context += `\n\n--- Source CMDA Context ---`;
      context += `\nCMDA Rating: ${selectedCmda.overallScore || "Unknown"}`;
      if (selectedCmda.controlDescription) {
        context += `\nControl Description: ${selectedCmda.controlDescription}`;
      }
      if (selectedCmda.monitoringToolDescription) {
        context += `\nMonitoring Tool Description: ${selectedCmda.monitoringToolDescription}`;
      }
    }

    if (!processDesc && !riskConsequences && !controlIds) {
      message.warning("Fill in Process & Risk information first");
      return;
    }
    const nonAnalyzable: string[] = [];
    if (processDesc && !isReviewAnalyzable(processDesc))
      nonAnalyzable.push("Process Initial Review");
    if (riskConsequences && !isReviewAnalyzable(riskConsequences))
      nonAnalyzable.push("Risk Initial Review");
    if (nonAnalyzable.length > 0) {
      message.warning(
        `${nonAnalyzable.join(" and ")} ${nonAnalyzable.length > 1 ? "are" : "is"} too brief or non-substantive to analyze. Add concrete details (who, when, how, evidence, gaps) before running AI.`,
      );
      return;
    }
    setCmaActivating(true);
    try {
      const [ctlRecs, monRecs] = await Promise.all([
        suggestCMARecommendations(context),
        suggestCMARecommendations(context),
      ]);
      setCmaRecommendations({ control: ctlRecs, monitoring: monRecs });
      setCmaActivated(true);
      setRecState({});
      message.success(
        "AI recommendations ready — Initial Review fields will be pre-filled",
      );
    } catch (e) {
      message.error("AI activation failed");
      console.error(e);
    } finally {
      setCmaActivating(false);
    }
  };

  const generateProposedFromReview = async (
    scope: "process" | "risk",
  ): Promise<void> => {
    const reviewField =
      scope === "process" ? "processInitialReview" : "riskInitialReview";
    const targetField =
      scope === "process" ? "processProposedChanges" : "riskProposedChanges";
    const label = scope === "process" ? "Process" : "Risk";
    const reviewText = (form.getFieldValue(reviewField) || "").trim();

    if (!reviewText) {
      message.warning(`Fill in ${label} Initial Review first`);
      return;
    }
    if (!isReviewAnalyzable(reviewText)) {
      message.warning(
        `${label} Initial Review is too brief or non-substantive to analyze. Describe who performs the activity, how often, what evidence is captured, and known gaps.`,
      );
      return;
    }

    const setLoadingFlag =
      scope === "process"
        ? setAnalyzingProcessProposed
        : setAnalyzingRiskProposed;
    setLoadingFlag(true);
    try {
      const context = `${label} Initial Review: ${reviewText}\n\nGenerate concrete proposed changes to address gaps in the review above.`;
      const recs = await suggestCMARecommendations(context);
      const proposed =
        recs && recs.length > 0
          ? recs
              .map(
                (r) =>
                  `• ${r.dimension}: ${r.suggestion}${r.rationale ? ` — ${r.rationale}` : ""}`,
              )
              .join("\n")
          : "";
      if (!proposed) {
        message.warning("AI returned no suggestions");
        return;
      }
      form.setFieldsValue({ [targetField]: proposed });
      message.success(`${label} Proposed Changes generated`);
    } catch (e) {
      console.error(e);
      message.error("AI analysis failed");
    } finally {
      setLoadingFlag(false);
    }
  };

  const handleCmaTypeChange = (newType: string) => {
    if (newType === "change" && form.getFieldValue("linkedCmdaId")) {
      form.setFieldsValue({
        linkedCmdaId: undefined,
        cmaControlIds: "",
        cmaMonitoringIds: "",
        controlId: "",
        monitoringToolId: "",
        controlDescription: "",
        monitoringToolDescription: "",
        teamMembers: "",
        processId: "",
        activityDescription: "",
        processInitialReview: "",
        riskInitialReview: "",
        riskIds: "",
      });
      setSelectedCmda(null);
      setCmaQuestionPrefills({});
      message.info("Linked CMDA cleared — fill fields manually");
    }
  };

  const handleCmdaSelect = async (cmdaId: string) => {
    if (!cmdaId) {
      setSelectedCmda(null);
      return;
    }
    try {
      const cmda = await apiService.getAssessment(cmdaId);
      setSelectedCmda(cmda);
      form.setFieldsValue({
        cmaControlIds: cmda.controlId || "",
        cmaMonitoringIds: cmda.monitoringToolId || "",
        controlId: cmda.controlId || "",
        monitoringToolId: cmda.monitoringToolId || "",
        controlDescription: cmda.controlDescription || "",
        monitoringToolDescription: cmda.monitoringToolDescription || "",
        teamMembers: cmda.teamMembers || "",
        linkedCmdaId: cmda.assessmentId,
        cmaType: "remediation",
        processId: cmda.processId || "",
        activityDescription: cmda.activityDescription || "",
        processInitialReview:
          cmda.processDescription || cmda.controlDescription || "",
        riskInitialReview: cmda.riskConsequences || "",
        riskIds: Array.isArray(cmda.riskIds)
          ? cmda.riskIds.join(", ")
          : cmda.riskIds || "",
      });
      // Reset prior CMA per-question prefills so the next entry to control/
      // monitoring step re-runs against the new description.
      setCmaQuestionPrefills({});
      message.success(`Loaded data from CMDA: ${cmda.assessmentName}`);
    } catch (err) {
      console.error("Failed to load CMDA:", err);
      message.error("Failed to load CMDA data");
    }
  };

  const applyRecommendation = (
    scope: "control" | "monitoring",
    index: number,
    text: string,
  ) => {
    const fieldName =
      scope === "control" ? "controlResponses" : "monitoringResponses";
    const list = form.getFieldValue(fieldName) || [];
    const updated = [...list];
    if (updated[index]) {
      updated[index] = { ...updated[index], proposedChanges: text };
      form.setFieldsValue({ [fieldName]: updated });
    }
    setRecState((prev) => ({ ...prev, [`${scope}-${index}`]: { used: true } }));
  };

  const dismissRecommendation = (
    scope: "control" | "monitoring",
    index: number,
  ) => {
    setRecState((prev) => ({
      ...prev,
      [`${scope}-${index}`]: { dismissed: true },
    }));
  };

  const handleCMDAResponseChange = (
    questionId: string,
    newResponse: string,
    listName: "controlResponses" | "monitoringResponses",
    index: number,
  ) => {
    const prefill = aiPrefills[questionId];
    const overriding = !!(prefill && prefill.response !== newResponse);
    setAiOverrides((prev) => ({ ...prev, [questionId]: overriding }));
    const cur = form.getFieldValue(listName) as any[];
    if (cur?.[index] !== undefined) {
      cur[index] = {
        ...cur[index],
        aiAnswer: overriding ? prefill!.response : undefined,
      };
      form.setFieldsValue({ [listName]: [...cur] });
    }
    // Track max-1-N/A-per-section constraint
    if (listName === "controlResponses") {
      if (newResponse === "NA") setControlNAQid(questionId);
      else if (controlNAQid === questionId) setControlNAQid(null);
    } else {
      if (newResponse === "NA") setMonitoringNAQid(questionId);
      else if (monitoringNAQid === questionId) setMonitoringNAQid(null);
    }
  };

  const handleSave = async (
    status: "draft" | "submitted",
    options: { silent?: boolean; autoSave?: boolean } = {},
  ) => {
    const { silent = false, autoSave = false } = options;
    // autoSave implies silent (no navigation), and both skip full validation
    const skipMessages = autoSave;
    const skipNavigate = silent || autoSave;
    const skipValidate = silent || autoSave;
    try {
      setLoading(true);

      if (!skipValidate) {
        await form.validateFields();
      }

      // Use getFieldsValue(true) to get ALL form values including unmounted steps
      const allFormValues = form.getFieldsValue(true);

      // Additional validation for submitted assessments
      if (status === "submitted") {
        // Check final comment
        if (
          !allFormValues.finalAssessmentComment ||
          !allFormValues.finalAssessmentComment.trim()
        ) {
          message.error(
            "Please provide a final assessment comment before submitting",
          );
          setLoading(false);
          return;
        }

        // Check all questions are answered - properly check each response
        const controlResponses = allFormValues.controlResponses || [];
        const monitoringResponses = allFormValues.monitoringResponses || [];

        // Get assessment type from values or currentType state
        const assessmentType = allFormValues.assessmentType || currentType;

        if (assessmentType === "CMA") {
          // CMA: All questions must have proposedChanges filled.
          // If we skipped the Control Assessment step (remediation where
          // only monitoring failed in the linked CMDA), don't require
          // control responses.
          const allControlAnswered = skipCmaControl
            ? true
            : controlResponses.every((r: ControlResponse) => {
                return r.proposedChanges && r.proposedChanges.trim();
              });

          const allMonitoringAnswered = monitoringResponses.every(
            (r: MonitoringResponse) => {
              return r.proposedChanges && r.proposedChanges.trim();
            },
          );

          if (!allControlAnswered || !allMonitoringAnswered) {
            message.error(
              "Please provide proposed changes for all questions before submitting",
            );
            setLoading(false);
            return;
          }
        } else if (assessmentType === "CMDA") {
          // CMDA: At least 17 out of 18 control questions must be answered, all 9 monitoring questions must be answered
          const answeredControlCount = controlResponses.filter(
            (r: ControlResponse) =>
              r.response === "Y" || r.response === "N" || r.response === "NA",
          ).length;

          const allMonitoringAnswered = monitoringResponses.every(
            (r: MonitoringResponse) =>
              r.response === "Y" || r.response === "N" || r.response === "NA",
          );

          if (answeredControlCount < 17) {
            message.error(
              "At least 17 out of 18 control questions must be answered before submitting",
            );
            setLoading(false);
            return;
          }

          if (!allMonitoringAnswered) {
            message.error(
              "All 9 monitoring questions must be answered before submitting",
            );
            setLoading(false);
            return;
          }
        }
      }

      // Team members: simple string field
      const teamMembersText =
        typeof allFormValues.teamMembers === "string"
          ? allFormValues.teamMembers.trim()
          : "";

      const riskIdsArray =
        typeof allFormValues.riskIds === "string"
          ? allFormValues.riskIds
              .split(",")
              .map((id: string) => id.trim())
              .filter((id: string) => id.length > 0)
          : Array.isArray(allFormValues.riskIds)
            ? allFormValues.riskIds
            : [];

      const assessmentData: Partial<Assessment> = {
        assessmentName: allFormValues.assessmentName,
        assessmentType: allFormValues.assessmentType || currentType,
        assessorId: user?.username || "unknown",
        assessorName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        status,
        controlResponses: allFormValues.controlResponses || [],
        monitoringResponses: allFormValues.monitoringResponses || [],
        teamMembers: teamMembersText,
        finalAssessmentComment: allFormValues.finalAssessmentComment || "",
        // CMA fields
        processId: allFormValues.processId || "",
        processDescription: allFormValues.processDescription || "",
        activityDescription: allFormValues.activityDescription || "",
        riskIds: riskIdsArray,
        riskConsequences: allFormValues.riskConsequences || "",
        processInitialReview: allFormValues.processInitialReview || "",
        processProposedChanges: allFormValues.processProposedChanges || "",
        riskInitialReview: allFormValues.riskInitialReview || "",
        riskProposedChanges: allFormValues.riskProposedChanges || "",
        // CMDA CDA fields
        controlDesignRating: allFormValues.controlDesignRating,
        controlIssueId: allFormValues.controlIssueId || "",
        designCompletionDate: allFormValues.designCompletionDate || "",
        facilitatorName: allFormValues.facilitatorName || "",
        facilitatorEmployeeId: allFormValues.facilitatorEmployeeId || "",
        facilitatorKnowledgeable: allFormValues.facilitatorKnowledgeable,
        designParticipants: allFormValues.designParticipants || "",
        controlPerformerRepresentative:
          allFormValues.controlPerformerRepresentative || "",
        controlId: allFormValues.controlId || "",
        controlDescription: allFormValues.controlDescription || "",
        regulationObligationIds: allFormValues.regulationObligationIds || "",
        // CMDA MDA fields
        monitoringDesignRating: allFormValues.monitoringDesignRating,
        monitoringIssueId: allFormValues.monitoringIssueId || "",
        monitoringToolId: allFormValues.monitoringToolId || "",
        monitoringToolDescription:
          allFormValues.monitoringToolDescription || "",
        // CMA linking fields
        linkedCmdaId: allFormValues.linkedCmdaId || "",
        cmaControlIds: allFormValues.cmaControlIds || "",
        cmaMonitoringIds: allFormValues.cmaMonitoringIds || "",
        cmaType: allFormValues.cmaType || "",
      };

      const effectiveId = assessmentId || savedAssessmentId;
      if (effectiveId) {
        await apiService.updateAssessment(effectiveId, assessmentData);
        if (!skipMessages) {
          message.success(
            skipNavigate ? "Progress saved" : "Assessment updated successfully",
          );
        }
      } else {
        const created = await apiService.createAssessment(assessmentData);
        if (created?.assessmentId) {
          setSavedAssessmentId(created.assessmentId);
        }
        if (!skipMessages) {
          message.success(
            skipNavigate ? "Progress saved" : "Assessment created successfully",
          );
        }
      }

      if (!skipNavigate) {
        navigate("/assessments");
      }
    } catch (error: any) {
      console.error("Error saving assessment:", error);
      if (!skipMessages) {
        const errMsg =
          error?.response?.data?.error ||
          error?.message ||
          "Failed to save assessment";
        message.error(errMsg);
      }
      if (autoSave) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  // When CMA is a remediation linked to a CMDA where the control was
  // adequate but monitoring was inadequate, skip the CMA Control Assessment
  // step — only monitoring needs to be reassessed.
  const skipCmaControl =
    currentType === "CMA" &&
    watchedCmaType === "remediation" &&
    !!selectedCmda &&
    selectedCmda.controlDesignRating !== "Inadequate" &&
    selectedCmda.monitoringDesignRating === "Inadequate";

  const steps =
    currentType === "CMA"
      ? [
          {
            key: "basic",
            title: "Basic Information",
            description: "Assessment details",
          },
          {
            key: "process",
            title: "Process & Risk Information",
            description: "Process and risk details",
          },
          ...(skipCmaControl
            ? []
            : [
                {
                  key: "control",
                  title: "Control Assessment",
                  description:
                    "5 Control questions (WHO, WHEN, HOW, WHERE, WHAT)",
                },
              ]),
          {
            key: "monitoring",
            title: "Monitoring Assessment",
            description: "5 Monitoring questions (WHO, WHEN, HOW, WHERE, WHAT)",
          },
          {
            key: "review",
            title: "Review & Submit",
            description: "Final review",
          },
        ]
      : [
          {
            title: "Basic Information",
            description: "Assessment details",
          },
          {
            title: `${t.section_cda} (CDA)`,
            description: "CDA",
          },
          {
            title: t.section_cda_questions,
            description: "18 Control questions",
          },
          {
            title: `${t.section_mda} (MDA)`,
            description: "MDA",
          },
          {
            title: t.section_mda_questions,
            description: "9 Monitoring questions",
          },
          {
            title: "Review & Submit",
            description: "Final review",
          },
        ];

  const renderStepper = () => (
    <div className="v3-stepper">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="v3-step">
            <div className="v3-step-inner">
              <div
                className={`v3-step-circle ${index === currentStep ? "active" : index < currentStep ? "done" : ""}`}
              >
                {index < currentStep ? "\u2713" : index + 1}
              </div>
              <div
                className={`v3-step-label ${index === currentStep ? "active" : index < currentStep ? "done" : ""}`}
              >
                {step.title}
              </div>
              <div className="v3-step-sub">{step.description}</div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`v3-step-connector ${index < currentStep ? "done" : ""}`}
              />
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const renderBasicInfo = () => (
    <div className="v3-form-section">
      <div className="v3-form-section-title">Assessment Information</div>
      <div className="v3-form-group">
        <Form.Item
          name="assessmentName"
          label={
            <span className="v3-form-label">
              Assessment Name <span className="req">*</span>
            </span>
          }
          rules={[{ required: true, message: "Please enter assessment name" }]}
        >
          <Input placeholder="Enter assessment name" />
        </Form.Item>
      </div>
      {!typePreselected && (
        <div className="v3-form-group">
          <Form.Item
            name="assessmentType"
            label={
              <span className="v3-form-label">
                Assessment Type <span className="req">*</span>
              </span>
            }
            rules={[
              { required: true, message: "Please select assessment type" },
            ]}
          >
            <Radio.Group>
              <div className="v3-radio-card-group">
                <label
                  className={`v3-radio-card ${currentType === "CMDA" ? "selected" : ""}`}
                >
                  <Radio value="CMDA" />
                  <div>
                    <div className="v3-radio-card-label">CMDA</div>
                    <div className="v3-radio-card-sub">{t.doc_cmda_title}</div>
                  </div>
                </label>
                <label
                  className={`v3-radio-card ${currentType === "CMA" ? "selected" : ""}`}
                >
                  <Radio value="CMA" />
                  <div>
                    <div className="v3-radio-card-label">CMA</div>
                    <div className="v3-radio-card-sub">{t.doc_cma_title}</div>
                  </div>
                </label>
              </div>
            </Radio.Group>
          </Form.Item>
        </div>
      )}
      {typePreselected && (
        <Form.Item name="assessmentType" hidden>
          <Input />
        </Form.Item>
      )}
      {currentType !== "CMA" && (
        <div className="v3-form-group">
          <Form.Item
            name="teamMembers"
            label={
              <span className="v3-form-label">
                Team Members{" "}
                <span style={{ color: "var(--v3-gray-400)", fontWeight: 400 }}>
                  (Optional)
                </span>
              </span>
            }
          >
            <Input placeholder="Enter team member names (e.g., John Doe, Jane Smith)" />
          </Form.Item>
        </div>
      )}

      {/* CMA-specific fields */}
      {currentType === "CMA" && (
        <>
          <div className="v3-form-group">
            <Form.Item
              name="cmaType"
              label={
                <span className="v3-form-label">
                  CMA Type <span className="req">*</span>
                </span>
              }
              rules={[
                {
                  required: currentType === "CMA",
                  message: "Please select CMA type",
                },
              ]}
            >
              <Radio.Group
                onChange={(e) => handleCmaTypeChange(e.target.value)}
                disabled={isEdit}
              >
                <div className="v3-radio-card-group">
                  <label
                    className={`v3-radio-card ${watchedCmaType === "remediation" ? "selected" : ""}${isEdit ? " disabled" : ""}`}
                  >
                    <Radio value="remediation" />
                    <div>
                      <div className="v3-radio-card-label">Remediation</div>
                      <div className="v3-radio-card-sub">
                        CMDA/MDA returned {t.rating_inadequate} — fixing a gap
                      </div>
                    </div>
                  </label>
                  <label
                    className={`v3-radio-card ${watchedCmaType === "change" ? "selected" : ""}${isEdit ? " disabled" : ""}`}
                  >
                    <Radio value="change" />
                    <div>
                      <div className="v3-radio-card-label">
                        Change Management
                      </div>
                      <div className="v3-radio-card-sub">
                        Control or monitoring needs updating
                      </div>
                    </div>
                  </label>
                </div>
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="v3-form-group">
            <Form.Item
              name="teamMembers"
              label={
                <span className="v3-form-label">
                  Team Members{" "}
                  <span
                    style={{ color: "var(--v3-gray-400)", fontWeight: 400 }}
                  >
                    (Optional)
                  </span>
                </span>
              }
            >
              <Input placeholder="Enter team member names (e.g., John Doe, Jane Smith)" />
            </Form.Item>
          </div>

          {watchedCmaType === "remediation" && (
            <div className="v3-form-group">
              {isEdit ? (
                <>
                  <Form.Item name="linkedCmdaId" hidden>
                    <Input />
                  </Form.Item>
                  <div className="v3-form-group">
                    <div className="v3-form-label" style={{ marginBottom: 4 }}>
                      Linked CMDA
                    </div>
                    <Input
                      disabled
                      value={selectedCmda?.assessmentName || "Loading..."}
                    />
                  </div>
                </>
              ) : (
                <Form.Item
                  name="linkedCmdaId"
                  label={
                    <span className="v3-form-label">
                      Link to Failed CMDA{" "}
                      <span
                        style={{ color: "var(--v3-gray-400)", fontWeight: 400 }}
                      >
                        (Optional — prefills fields from selected CMDA)
                      </span>
                    </span>
                  }
                >
                  <Select
                    placeholder={`Select an ${t.rating_inadequate} CMDA to link...`}
                    allowClear
                    loading={loadingCmdas}
                    onChange={handleCmdaSelect}
                    showSearch
                    optionFilterProp="children"
                  >
                    {failedCmdas.map((cmda) => (
                      <Select.Option
                        key={cmda.assessmentId}
                        value={cmda.assessmentId}
                      >
                        {cmda.assessmentName} —{" "}
                        {cmda.controlId || "No Control ID"} (
                        {t.rating_inadequate})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>
          )}

          <div className="v3-form-row">
            <div className="v3-form-group">
              <Form.Item
                name="cmaControlIds"
                label={
                  <span className="v3-form-label">
                    Control ID(s) <span className="req">*</span>
                  </span>
                }
                rules={[
                  {
                    required: currentType === "CMA",
                    message: "Please enter Control ID(s)",
                  },
                ]}
              >
                <Input
                  placeholder="e.g., PROMO-003"
                  style={{ maxWidth: 320 }}
                />
              </Form.Item>
            </div>
            <div className="v3-form-group">
              <Form.Item
                name="cmaMonitoringIds"
                label={
                  <span className="v3-form-label">
                    Monitoring ID(s) <span className="req">*</span>
                  </span>
                }
                rules={[
                  {
                    required: currentType === "CMA",
                    message: "Please enter Monitoring ID(s)",
                  },
                ]}
              >
                <Input placeholder="e.g., 1427077" style={{ maxWidth: 320 }} />
              </Form.Item>
            </div>
          </div>

          <div className="v3-form-group">
            <Form.Item
              name="controlDescription"
              label={
                <span className="v3-form-label">
                  Control Description <span className="req">*</span>
                </span>
              }
              rules={[
                {
                  required: currentType === "CMA",
                  message: "Please provide control description",
                },
              ]}
            >
              <TextArea
                rows={5}
                placeholder="Describe the control activity that the AI will use to assess each Control Assessment question"
              />
            </Form.Item>
          </div>

          <div className="v3-form-group">
            <Form.Item
              name="monitoringToolDescription"
              label={
                <span className="v3-form-label">
                  Monitoring Tool Description <span className="req">*</span>
                </span>
              }
              rules={[
                {
                  required: currentType === "CMA",
                  message: "Please provide monitoring tool description",
                },
              ]}
            >
              <TextArea
                rows={5}
                placeholder="Describe the monitoring tool that the AI will use to assess each Monitoring Assessment question"
              />
            </Form.Item>
          </div>
        </>
      )}
    </div>
  );

  const renderProcessRiskInfo = () => (
    <div>
      <div className="v3-section-header">
        <div className="v3-section-title">Process & Risk Information</div>
        <div className="v3-section-sub">
          Enter the process and risk details for this assessment.
        </div>
      </div>
      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="processId"
            label="Process ID"
            rules={[
              {
                required: currentType === "CMA",
                message: "Please provide a process ID",
              },
            ]}
          >
            <Input placeholder="Enter process ID" />
          </Form.Item>
        </div>
        <div className="v3-form-group">
          <Form.Item
            name="activityDescription"
            label="Activity Description"
            rules={[
              {
                required: currentType === "CMA",
                message: "Please describe the activity",
              },
            ]}
          >
            <Input placeholder="Describe the control or monitoring activity" />
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="riskIds"
            label="Risk IDs"
            tooltip="Separate multiple risk IDs with commas"
            rules={[
              {
                required: currentType === "CMA",
                message: "Please provide associated risk IDs",
              },
            ]}
          >
            <Input placeholder="e.g., RISK-001, RISK-005" />
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-divider">Process Reviews</div>

      <div className="v3-form-group">
        <Form.Item
          name="processInitialReview"
          label="Process Initial Review"
          rules={[
            {
              required: currentType === "CMA",
              message: "Please provide an initial review",
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Initial review notes for the process"
          />
        </Form.Item>
        <div style={{ marginTop: -8, marginBottom: 12 }}>
          <button
            type="button"
            className="v3-btn v3-btn-outline"
            onClick={() => generateProposedFromReview("process")}
            disabled={analyzingProcessProposed}
          >
            {analyzingProcessProposed
              ? "Analyzing..."
              : "Analyze with AI → Proposed Changes"}
          </button>
        </div>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="processProposedChanges"
          label="Process Proposed Changes"
        >
          <TextArea rows={4} placeholder="Proposed changes for the process" />
        </Form.Item>
      </div>

      <div className="v3-form-divider">Risk Reviews</div>

      <div className="v3-form-group">
        <Form.Item
          name="riskInitialReview"
          label="Risk Initial Review"
          rules={[
            {
              required: currentType === "CMA",
              message: "Please provide a risk initial review",
            },
          ]}
        >
          <TextArea rows={4} placeholder="Initial review notes for the risk" />
        </Form.Item>
        <div style={{ marginTop: -8, marginBottom: 12 }}>
          <button
            type="button"
            className="v3-btn v3-btn-outline"
            onClick={() => generateProposedFromReview("risk")}
            disabled={analyzingRiskProposed}
          >
            {analyzingRiskProposed
              ? "Analyzing..."
              : "Analyze with AI → Proposed Changes"}
          </button>
        </div>
      </div>

      <div className="v3-form-group">
        <Form.Item name="riskProposedChanges" label="Risk Proposed Changes">
          <TextArea rows={4} placeholder="Proposed changes for the risk" />
        </Form.Item>
      </div>

      {/* AI analysis trigger for CMA */}
      {currentType === "CMA" && !cmaActivated && (
        <div style={{ marginTop: 8 }}>
          <AITriggerBar
            title="Ready to activate AI analysis"
            subtitle="AI will pre-fill Initial Review fields in Control and Monitoring Assessment steps using Control ID, Monitoring ID, and the process context above."
            ctaLabel="Activate AI"
            processing={cmaActivating}
            onClick={activateCMAAI}
          />
        </div>
      )}

      {/* AI result panel after activation */}
      {currentType === "CMA" && cmaActivated && (
        <div style={{ marginTop: 12 }}>
          <AIPanel
            title={
              selectedCmda
                ? `Context loaded from CMDA: ${selectedCmda.assessmentName}`
                : "AI analysis complete — Initial Review fields pre-filled"
            }
            confidence={85}
          >
            <p>
              {selectedCmda
                ? `Control ${form.getFieldValue("cmaControlIds") || ""} and Monitoring ${form.getFieldValue("cmaMonitoringIds") || ""} loaded from CMDA record. CMDA rating: ${selectedCmda.overallScore || "Unknown"}. `
                : ""}
              AI has pre-populated recommendation panels across Control
              Assessment (WHO &rarr; WHAT) and Monitoring Assessment (WHO &rarr;
              WHAT). Review and use suggested Proposed Changes on each
              dimension. Proceed to Control Assessment.
            </p>
          </AIPanel>
        </div>
      )}
    </div>
  );

  const renderCDA = () => (
    <div>
      <div className="v3-section-header">
        <div className="v3-section-title">{t.section_cda} (CDA)</div>
        <div className="v3-section-sub">
          Enter the control details. AI analysis activates when you click
          Analyse.
        </div>
      </div>

      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="processId"
            label="Process ID"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please provide a process ID",
              },
            ]}
          >
            <Input placeholder="Enter process ID" />
          </Form.Item>
        </div>
        <div className="v3-form-group">
          <Form.Item
            name="activityDescription"
            label="Activity Description"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please describe the activity",
              },
            ]}
          >
            <Input placeholder="Describe the control or monitoring activity" />
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="processDescription"
            label="Process Description"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please describe the process",
              },
            ]}
          >
            <TextArea rows={4} placeholder="Provide process details" />
          </Form.Item>
        </div>
        <div className="v3-form-group">
          <Form.Item
            name="riskConsequences"
            label="Risk Consequences"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please describe risk consequences",
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe potential risk consequences"
            />
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="riskIds"
          label="Risk IDs"
          tooltip="Separate multiple risk IDs with commas"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please provide associated risk IDs",
            },
          ]}
        >
          <Input placeholder="e.g., RISK-001, RISK-005" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="controlIssueId"
          label={`${t.issue_label} ID (Optional)`}
          tooltip={`Cite comments and ${t.issue_label} ID here if applicable.`}
        >
          <Input placeholder={`Enter ${t.issue_label} ID (e.g., XXXX)`} />
        </Form.Item>
      </div>

      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="designCompletionDate"
            label="Design Completion Date"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please provide design completion date",
              },
            ]}
          >
            <Input type="date" />
          </Form.Item>
        </div>
        <div className="v3-form-group">
          <Form.Item
            name="facilitatorKnowledgeable"
            label="Is the facilitator (Design Assessor) knowledgeable about the company or business documented Activity/Process, Risks, and Controls?"
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please answer this question",
              },
            ]}
          >
            <Radio.Group>
              <Radio value="Yes">Yes</Radio>
              <Radio value="No">No</Radio>
            </Radio.Group>
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-row">
        <div className="v3-form-group">
          <Form.Item
            name="facilitatorName"
            label={`${t.facilitator_label} - Name`}
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please provide facilitator name",
              },
            ]}
          >
            <Input placeholder="Enter facilitator name" />
          </Form.Item>
        </div>
        <div className="v3-form-group">
          <Form.Item
            name="facilitatorEmployeeId"
            label={`${t.facilitator_label} - Employee ID`}
            rules={[
              {
                required: currentType === "CMDA",
                message: "Please provide employee ID",
              },
            ]}
          >
            <Input placeholder="Enter employee ID" />
          </Form.Item>
        </div>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="designParticipants"
          label="Design Participants (List employee IDs with comma space)"
          tooltip="List all Employee ID(s) with comma space"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please list design participants",
            },
          ]}
        >
          <Input placeholder="e.g., EID001, EID002, EID003" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="controlPerformerRepresentative"
          label={`Is there a representative for the ${t.performer_label} in the session? (List Employee ID with comma space)`}
          tooltip="List Employee ID(s) with comma space. If control is automated, participant should be someone who has in-depth knowledge of the systemic control design."
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please list control performer representative(s)",
            },
          ]}
        >
          <Input placeholder="e.g., EID001, EID002" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="controlId"
          label="Control ID"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please provide control ID",
            },
          ]}
        >
          <Input placeholder="Enter Control ID (e.g., XXXXX)" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="controlDescription"
          label="Control Description"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please provide control description",
            },
          ]}
          initialValue="On a weekly basis, after the US_Cancelled_Case_Report has been uploaded to the Control MIS system, the Operations Dispute agent reviews the report for cancelled cases. The agent will use the Case ID to access the account in Oracle to determine the exact date/time when the case was submitted. The agent will then locate the call in Cisco using the date, time and agent ID and listen to the call interaction where the case was cancelled and update Zendesk to indicate of remediation is required for the following Agent cancelled case and case was opened a subsequent day, Agent cancelled case and no new case opened, No call located and no case opened same day. The agent will review Oracle to confirm if any other cases were opened same day (confirm case contains same transaction from the cancelled case). If remediation is required, submit an Misc Case and include the required note and information. If remediation is not required place the applicable review note on the account. This Control is executed across NAM - US - Contact Center Operations and on behalf of NAM - US - Customer Service - Retail to ensure remediation for cancelled cases, if applicable, is completed accurately, correctly and timely and to remain compliant with Regulation E (implementing the Electronic Funds Transfer Act) 12 CFR Part 1005, Obligations: 1441005.34 Procedures for Cancellation and Refund of Remittance Transfers; 144353: Remittance Transfers - Accuracy"
        >
          <TextArea rows={6} placeholder="Enter detailed control description" />
        </Form.Item>
      </div>

      {/* AI analysis trigger + result */}
      <div className="v3-form-group">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            size="small"
            onClick={runCDAAnalysis}
            loading={aiAnalyzing}
          >
            Analyse with AI
          </Button>
          {aiAnalyzing && (
            <AIProcessing text="AI is analysing your control description..." />
          )}
        </div>
        {aiAnalysis && (
          <div style={{ marginTop: 12 }}>
            <AIAnalysisResult
              analysis={aiAnalysis}
              obligations={aiObligations || undefined}
            />
          </div>
        )}
      </div>

      {!t.req_hidden && (
        <div className="v3-form-group">
          <Form.Item
            name="regulationObligationIds"
            label={`All ${t.req_field_label} IDs mapped to this Control`}
            tooltip={t.req_field_placeholder}
            rules={[
              {
                required: currentType === "CMDA" && !t.req_hidden,
                message: `Please provide ${t.req_field_label} IDs`,
              },
            ]}
            initialValue="122608 - Regulation E (implementing the Electronic Funds Transfer Act) 12 CFR Part 1005
1441005 - Remittance Transfers: Disclosure Requirements - Long form error resolution and cancellation notice"
          >
            <TextArea rows={4} placeholder={t.req_source_hint} />
          </Form.Item>
        </div>
      )}
    </div>
  );

  const renderMDA = () => (
    <div>
      <div className="v3-section-header">
        <div className="v3-section-title">{t.section_mda} (MDA)</div>
        <div className="v3-section-sub">{t.section_mda} information.</div>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="monitoringIssueId"
          label="Issue ID (Optional)"
          tooltip="Cite Issue ID here if applicable."
        >
          <Input placeholder="Enter Issue ID (e.g., XXXX)" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="monitoringToolId"
          label="Monitoring Tool ID"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please provide monitoring tool ID",
            },
          ]}
        >
          <Input placeholder="Enter Monitoring Tool ID (e.g., XXXXX)" />
        </Form.Item>
      </div>

      <div className="v3-form-group">
        <Form.Item
          name="monitoringToolDescription"
          label="Monitoring Tool Description"
          rules={[
            {
              required: currentType === "CMDA",
              message: "Please provide monitoring tool description",
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Enter detailed monitoring tool description"
          />
        </Form.Item>
      </div>
    </div>
  );

  const renderControlQuestions = () => {
    const controlQuestions =
      currentType === "CMDA"
        ? CMDA_CONTROL_QUESTIONS.questions
        : CMA_ASSESSMENT_QUESTIONS.control;

    // Compute overall AI pre-fill confidence for control questions
    const controlPrefills =
      currentType === "CMDA"
        ? controlQuestions.map((q) => aiPrefills[q.id]).filter(Boolean)
        : [];
    const avgControlConfidence =
      controlPrefills.length > 0
        ? Math.round(
            controlPrefills.reduce((sum, p) => sum + (p?.confidence || 0), 0) /
              controlPrefills.length,
          )
        : 0;

    return (
      <div>
        <div className="v3-section-header">
          <div className="v3-section-title">
            {currentType === "CMDA"
              ? `${t.section_cda_questions} Questions`
              : `${t.section_cma_control} Questions`}
          </div>
          <div className="v3-section-sub">
            {currentType === "CMDA"
              ? "Answer the following questions about control design adequacy. AI has pre-filled answers where possible -- review and confirm each one."
              : "AI assesses each question using the Control Description. Where evidence is missing, you'll see a clear 'Unable to determine — please review manually' placeholder."}
          </div>
        </div>

        {currentType === "CMDA" &&
          aiAnalyzing &&
          controlPrefills.length === 0 && (
            <AIProcessing text="AI is pre-filling answers from your Control Description..." />
          )}

        {currentType === "CMA" && cmaPrefillLoading.control && (
          <AIProcessing text="AI is assessing each question from your Control Description..." />
        )}

        {currentType === "CMDA" && controlPrefills.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 16px",
              background:
                avgControlConfidence >= 85
                  ? "#f6ffed"
                  : avgControlConfidence >= 70
                    ? "#fffbe6"
                    : "#fff2e8",
              border: `1px solid ${avgControlConfidence >= 85 ? "#b7eb8f" : avgControlConfidence >= 70 ? "#ffe58f" : "#ffbb96"}`,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <ThunderboltOutlined
              style={{
                color:
                  avgControlConfidence >= 85
                    ? "#52c41a"
                    : avgControlConfidence >= 70
                      ? "#faad14"
                      : "#fa8c16",
              }}
            />
            <strong>AI Pre-fill Confidence: {avgControlConfidence}%</strong>
            <span style={{ fontSize: 12, color: "#888" }}>
              ({controlPrefills.length} questions pre-filled from Control
              Description)
            </span>
          </div>
        )}

        <Form.List name="controlResponses" key={`control-${currentType}`}>
          {(fields) => (
            <>
              {fields.map((field: any, index: number) => {
                const question = controlQuestions[index];
                const qid = question.id;
                const prefill = aiPrefills[qid];
                const overridden = aiOverrides[qid];
                const rec = cmaRecommendations.control[index];
                const recKey = `control-${index}`;
                const recS = recState[recKey] || {};

                const adapted = adaptQuestion(qid, question, t);
                return (
                  <div className="v3-q-card" key={field.key}>
                    <div className="v3-q-title">{adapted.question}</div>
                    {adapted.guidance && (
                      <div className="v3-q-guidance">
                        <strong>Guidance:</strong> {adapted.guidance}
                      </div>
                    )}
                    {currentType === "CMA" ? (
                      <>
                        {(() => {
                          const cmaCtlNAActive = Object.keys(cmaNA).find(
                            (k) => cmaNA[k] && CMA_ASSESSMENT_QUESTIONS.control.some((q) => q.id === k)
                          ) || null;
                          const otherCtlHasNA = cmaCtlNAActive !== null && cmaCtlNAActive !== qid;
                          return (
                            <div style={{ marginBottom: 8 }}>
                              <Button
                                size="small"
                                type={cmaNA[qid] ? "primary" : "default"}
                                disabled={otherCtlHasNA}
                                onClick={() => {
                                  const enabling = !cmaNA[qid];
                                  setCmaNA((prev) => ({ ...prev, [qid]: enabling }));
                                  const cur = form.getFieldValue("controlResponses") || [];
                                  const updated = cur.map((r: any, i: number) =>
                                    i === index
                                      ? { ...r, initialReview: enabling ? "N/A" : "", proposedChanges: enabling ? "N/A" : "" }
                                      : r
                                  );
                                  form.setFieldsValue({ controlResponses: updated });
                                }}
                              >
                                {cmaNA[qid] ? "Clear N/A" : "Mark as N/A"}
                              </Button>
                            </div>
                          );
                        })()}
                        <div className="v3-form-group">
                          <Form.Item
                            {...field}
                            name={[field.name, "initialReview"]}
                            label="Control Assessment"
                            className={
                              cmaQuestionPrefills[qid]?.sufficient
                                ? "ai-filled-textarea"
                                : undefined
                            }
                            rules={[
                              {
                                required: !cmaNA[qid],
                                message: "Please provide a control assessment",
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              disabled={cmaNA[qid]}
                              placeholder="AI will fill from Control Description on entry, or provide your own..."
                            />
                          </Form.Item>
                          {cmaQuestionPrefills[qid] && (
                            <div
                              style={{
                                fontSize: 12,
                                color: cmaQuestionPrefills[qid].sufficient
                                  ? "#52c41a"
                                  : "#fa8c16",
                                marginTop: 4,
                              }}
                            >
                              {cmaQuestionPrefills[qid].sufficient
                                ? `AI confidence: ${cmaQuestionPrefills[qid].confidence}%`
                                : "AI: insufficient evidence in Control Description — please review manually"}
                            </div>
                          )}
                        </div>
                        {cmaActivated && rec && (
                          <AIRecommendationPanel
                            recommendation={rec}
                            used={recS.used}
                            dismissed={recS.dismissed}
                            onUse={(text) =>
                              applyRecommendation("control", index, text)
                            }
                            onDismiss={() =>
                              dismissRecommendation("control", index)
                            }
                          />
                        )}
                        <div className="v3-form-group">
                          <Form.Item
                            {...field}
                            name={[field.name, "proposedChanges"]}
                            label="Proposed Changes"
                            rules={[
                              {
                                required: !cmaNA[qid],
                                message: "Please provide proposed changes",
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              disabled={cmaNA[qid]}
                              placeholder="Enter proposed changes for this question..."
                            />
                          </Form.Item>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="v3-q-answer-row">
                          <Form.Item
                            {...field}
                            name={[field.name, "response"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select a response",
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Radio.Group
                              onChange={(e) =>
                                handleCMDAResponseChange(
                                  qid,
                                  e.target.value,
                                  "controlResponses",
                                  index,
                                )
                              }
                            >
                              <Radio value="Y">Yes</Radio>
                              <Radio value="N">No</Radio>
                              <Radio
                                value="NA"
                                disabled={controlNAQid !== null && controlNAQid !== qid}
                              >N/A</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {prefill && (
                            <AIInlineConfidence value={prefill.confidence} />
                          )}
                        </div>
                        <AIOverrideNotice show={!!overridden} />
                        <Form.Item
                          {...field}
                          name={[field.name, "aiAnswer"]}
                          style={{ display: "none" }}
                        >
                          <input type="hidden" />
                        </Form.Item>
                        <div
                          className="v3-form-group"
                          style={{ marginTop: 12 }}
                        >
                          <div className="v3-q-comment-label">
                            Comments (Optional)
                          </div>
                          <Form.Item {...field} name={[field.name, "comments"]}>
                            <TextArea
                              rows={2}
                              placeholder="Add any additional comments..."
                            />
                          </Form.Item>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </Form.List>
      </div>
    );
  };

  const renderMonitoringQuestions = () => {
    const monitoringQuestions =
      currentType === "CMDA"
        ? CMDA_MONITORING_QUESTIONS.questions
        : CMA_ASSESSMENT_QUESTIONS.monitoring;

    const monPrefills =
      currentType === "CMDA"
        ? monitoringQuestions.map((q) => aiPrefills[q.id]).filter(Boolean)
        : [];
    const avgMonConfidence =
      monPrefills.length > 0
        ? Math.round(
            monPrefills.reduce((sum, p) => sum + (p?.confidence || 0), 0) /
              monPrefills.length,
          )
        : 0;

    return (
      <div>
        <div className="v3-section-header">
          <div className="v3-section-title">
            {currentType === "CMDA"
              ? `${t.section_mda_questions} Questions`
              : `${t.section_cma_monitoring} Questions`}
          </div>
          <div className="v3-section-sub">
            {currentType === "CMDA"
              ? "Answer the following questions about monitoring effectiveness. AI has pre-filled answers where possible -- review and confirm each one."
              : "AI assesses each question using the Monitoring Tool Description. Where evidence is missing, you'll see a clear 'Unable to determine — please review manually' placeholder."}
          </div>
        </div>

        {currentType === "CMDA" && aiAnalyzing && monPrefills.length === 0 && (
          <AIProcessing text="AI is pre-filling answers from your Control Description..." />
        )}

        {currentType === "CMA" && cmaPrefillLoading.monitoring && (
          <AIProcessing text="AI is assessing each question from your Monitoring Tool Description..." />
        )}

        {currentType === "CMDA" && monPrefills.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: "8px 16px",
              background:
                avgMonConfidence >= 85
                  ? "#f6ffed"
                  : avgMonConfidence >= 70
                    ? "#fffbe6"
                    : "#fff2e8",
              border: `1px solid ${avgMonConfidence >= 85 ? "#b7eb8f" : avgMonConfidence >= 70 ? "#ffe58f" : "#ffbb96"}`,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <ThunderboltOutlined
              style={{
                color:
                  avgMonConfidence >= 85
                    ? "#52c41a"
                    : avgMonConfidence >= 70
                      ? "#faad14"
                      : "#fa8c16",
              }}
            />
            <strong>AI Pre-fill Confidence: {avgMonConfidence}%</strong>
            <span style={{ fontSize: 12, color: "#888" }}>
              ({monPrefills.length} questions pre-filled from Monitoring Tool
              Description)
            </span>
          </div>
        )}

        <Form.List name="monitoringResponses" key={`monitor-${currentType}`}>
          {(fields) => (
            <>
              {fields.map((field: any, index: number) => {
                const question = monitoringQuestions[index];
                const qid = question.id;
                const prefill = aiPrefills[qid];
                const overridden = aiOverrides[qid];
                const rec = cmaRecommendations.monitoring[index];
                const recKey = `monitoring-${index}`;
                const recS = recState[recKey] || {};

                const adaptedMon = adaptQuestion(qid, question, t);
                return (
                  <div className="v3-q-card" key={field.key}>
                    <div className="v3-q-title">{adaptedMon.question}</div>
                    {adaptedMon.guidance && (
                      <div className="v3-q-guidance">
                        <strong>Guidance:</strong> {adaptedMon.guidance}
                      </div>
                    )}
                    {currentType === "CMA" ? (
                      <>
                        {(() => {
                          const cmaMonNAActive = Object.keys(cmaNA).find(
                            (k) => cmaNA[k] && CMA_ASSESSMENT_QUESTIONS.monitoring.some((q) => q.id === k)
                          ) || null;
                          const otherMonHasNA = cmaMonNAActive !== null && cmaMonNAActive !== qid;
                          return (
                            <div style={{ marginBottom: 8 }}>
                              <Button
                                size="small"
                                type={cmaNA[qid] ? "primary" : "default"}
                                disabled={otherMonHasNA}
                                onClick={() => {
                                  const enabling = !cmaNA[qid];
                                  setCmaNA((prev) => ({ ...prev, [qid]: enabling }));
                                  const cur = form.getFieldValue("monitoringResponses") || [];
                                  const updated = cur.map((r: any, i: number) =>
                                    i === index
                                      ? { ...r, initialReview: enabling ? "N/A" : "", proposedChanges: enabling ? "N/A" : "" }
                                      : r
                                  );
                                  form.setFieldsValue({ monitoringResponses: updated });
                                }}
                              >
                                {cmaNA[qid] ? "Clear N/A" : "Mark as N/A"}
                              </Button>
                            </div>
                          );
                        })()}
                        <div className="v3-form-group">
                          <Form.Item
                            {...field}
                            name={[field.name, "initialReview"]}
                            label="Monitoring Assessment"
                            className={
                              cmaQuestionPrefills[qid]?.sufficient
                                ? "ai-filled-textarea"
                                : undefined
                            }
                            rules={[
                              {
                                required: !cmaNA[qid],
                                message:
                                  "Please provide a monitoring assessment",
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              disabled={cmaNA[qid]}
                              placeholder="AI will fill from Monitoring Tool Description on entry, or provide your own..."
                            />
                          </Form.Item>
                          {cmaQuestionPrefills[qid] && (
                            <div
                              style={{
                                fontSize: 12,
                                color: cmaQuestionPrefills[qid].sufficient
                                  ? "#52c41a"
                                  : "#fa8c16",
                                marginTop: 4,
                              }}
                            >
                              {cmaQuestionPrefills[qid].sufficient
                                ? `AI confidence: ${cmaQuestionPrefills[qid].confidence}%`
                                : "AI: insufficient evidence in Monitoring Tool Description — please review manually"}
                            </div>
                          )}
                        </div>
                        {cmaActivated && rec && (
                          <AIRecommendationPanel
                            recommendation={rec}
                            used={recS.used}
                            dismissed={recS.dismissed}
                            onUse={(text) =>
                              applyRecommendation("monitoring", index, text)
                            }
                            onDismiss={() =>
                              dismissRecommendation("monitoring", index)
                            }
                          />
                        )}
                        <div className="v3-form-group">
                          <Form.Item
                            {...field}
                            name={[field.name, "proposedChanges"]}
                            label="Proposed Changes"
                            rules={[
                              {
                                required: !cmaNA[qid],
                                message: "Please provide proposed changes",
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              disabled={cmaNA[qid]}
                              placeholder="Enter proposed changes for this question..."
                            />
                          </Form.Item>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="v3-q-answer-row">
                          <Form.Item
                            {...field}
                            name={[field.name, "response"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select a response",
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <Radio.Group
                              onChange={(e) =>
                                handleCMDAResponseChange(
                                  qid,
                                  e.target.value,
                                  "monitoringResponses",
                                  index,
                                )
                              }
                            >
                              <Radio value="Y">Yes</Radio>
                              <Radio value="N">No</Radio>
                              <Radio
                                value="NA"
                                disabled={monitoringNAQid !== null && monitoringNAQid !== qid}
                              >N/A</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {prefill && (
                            <AIInlineConfidence value={prefill.confidence} />
                          )}
                        </div>
                        <AIOverrideNotice show={!!overridden} />
                        <Form.Item
                          {...field}
                          name={[field.name, "aiAnswer"]}
                          style={{ display: "none" }}
                        >
                          <input type="hidden" />
                        </Form.Item>
                        <div
                          className="v3-form-group"
                          style={{ marginTop: 12 }}
                        >
                          <div className="v3-q-comment-label">
                            Comments (Optional)
                          </div>
                          <Form.Item {...field} name={[field.name, "comments"]}>
                            <TextArea
                              rows={2}
                              placeholder="Add any additional comments..."
                            />
                          </Form.Item>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </Form.List>
      </div>
    );
  };

  const renderReview = () => {
    const values = form.getFieldsValue(true);

    if (currentType === "CMA") {
      // CMA: No scoring, just show all responses
      return (
        <div>
          <div className="v3-section-header">
            <div className="v3-section-title">Review & Submit</div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Assessment Name</div>
            <div className="v3-rating-detail">{values.assessmentName}</div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Assessment Type</div>
            <div className="v3-rating-detail">{values.assessmentType}</div>
          </div>

          {/* CMA Linking Info */}
          <div className="v3-rating-block">
            <div className="v3-rating-title">CMA Details</div>
            {values.linkedCmdaId && (
              <div className="v3-rating-detail">
                Linked CMDA:{" "}
                {selectedCmda?.assessmentName || values.linkedCmdaId}
              </div>
            )}
            <div className="v3-rating-detail">
              Control ID(s): {values.cmaControlIds || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              Monitoring ID(s): {values.cmaMonitoringIds || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              CMA Type:{" "}
              {values.cmaType === "remediation"
                ? "Remediation"
                : values.cmaType === "change"
                  ? "Change Management"
                  : "Not specified"}
            </div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Process & Risk Information</div>
            <div className="v3-rating-detail">
              Process ID: {values.processId || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              Activity Description:{" "}
              {values.activityDescription || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              Risk IDs: {values.riskIds || "Not provided"}
            </div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Process Reviews</div>
            <div className="v3-rating-detail">
              Initial Review: {values.processInitialReview || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              Proposed Changes:{" "}
              {values.processProposedChanges || "Not provided"}
            </div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Risk Reviews</div>
            <div className="v3-rating-detail">
              Initial Review: {values.riskInitialReview || "Not provided"}
            </div>
            <div className="v3-rating-detail">
              Proposed Changes: {values.riskProposedChanges || "Not provided"}
            </div>
          </div>

          <div className="v3-rating-block">
            <div className="v3-rating-title">Team Members</div>
            <div className="v3-rating-detail">
              {values.teamMembers &&
              typeof values.teamMembers === "string" &&
              values.teamMembers.trim()
                ? values.teamMembers
                : "No team members added"}
            </div>
          </div>

          <div className="v3-form-group" style={{ marginTop: 16 }}>
            <Form.Item
              name="finalAssessmentComment"
              label={
                <span className="v3-form-label">Final Assessment Comment</span>
              }
              rules={[]}
            >
              <TextArea
                rows={4}
                placeholder="Provide your final assessment comment before submitting for export..."
              />
            </Form.Item>
          </div>
        </div>
      );
    }

    // CMDA: Calculate score with special rules for C6, M7, M8
    const controlResponses = values.controlResponses || [];
    const monitoringResponses = values.monitoringResponses || [];

    // Check C6 (must be "No")
    const c6Response = controlResponses.find(
      (r: ControlResponse) => r.questionId === "C6",
    );
    const c6IsNo = c6Response?.response === "N";

    // Check M7 and M8 (both must be "No")
    const m7Response = monitoringResponses.find(
      (r: MonitoringResponse) => r.questionId === "M7",
    );
    const m8Response = monitoringResponses.find(
      (r: MonitoringResponse) => r.questionId === "M8",
    );
    const m7IsNo = m7Response?.response === "N";
    const m8IsNo = m8Response?.response === "N";

    // Count total answered control questions
    const controlTotalAnswered = controlResponses.filter(
      (r: ControlResponse) =>
        r.response === "Y" || r.response === "N" || r.response === "NA",
    ).length;

    // Count "Yes" answers for all control questions (for display)
    const controlYesCount = controlResponses.filter(
      (r: ControlResponse) => r.response === "Y",
    ).length;

    // Count all answered monitoring questions (all 9 must be answered)
    const monitoringTotalAnswered = monitoringResponses.filter(
      (r: MonitoringResponse) =>
        r.response === "Y" || r.response === "N" || r.response === "NA",
    ).length;

    // Count "Yes" answers for monitoring questions (excluding M7, M8 which must be No)
    const monitoringYesCount = monitoringResponses.filter(
      (r: MonitoringResponse) =>
        r.questionId !== "M7" && r.questionId !== "M8" && r.response === "Y",
    ).length;

    const requiredControl = controlTotalAnswered === 18 ? 17 : 16;
    const requiredMonitoring = 7;

    // Count "Yes" answers for other control questions (excluding C6) - for adequacy calculation
    const controlYesCountExcludingC6 = controlResponses.filter(
      (r: ControlResponse) => r.questionId !== "C6" && r.response === "Y",
    ).length;

    const controlAdequate =
      c6IsNo && controlYesCountExcludingC6 >= requiredControl;
    const monitoringAdequate =
      m7IsNo &&
      m8IsNo &&
      monitoringTotalAnswered === 9 &&
      monitoringYesCount >= requiredMonitoring;

    return (
      <div>
        <div className="v3-section-header">
          <div className="v3-section-title">Review & Submit</div>
        </div>

        <div className="v3-rating-block">
          <div className="v3-rating-title">Assessment Name</div>
          <div className="v3-rating-detail">{values.assessmentName}</div>
        </div>

        <div className="v3-rating-block">
          <div className="v3-rating-title">Assessment Type</div>
          <div className="v3-rating-detail">{values.assessmentType}</div>
        </div>

        {/* CDA Information */}
        <div className="v3-rating-block">
          <div className="v3-rating-title">{t.section_cda} (CDA)</div>
          {values.controlIssueId && (
            <div className="v3-rating-detail">
              {t.issue_label} ID: {values.controlIssueId}
            </div>
          )}
          <div className="v3-rating-detail">
            Design Completion Date:{" "}
            {values.designCompletionDate || "Not provided"}
          </div>
          <div className="v3-rating-detail">
            {t.facilitator_label}: {values.facilitatorName || "Not provided"} (
            {values.facilitatorEmployeeId || "N/A"})
          </div>
          <div className="v3-rating-detail">
            {t.facilitator_label} Knowledgeable:{" "}
            {values.facilitatorKnowledgeable || "Not provided"}
          </div>
          <div className="v3-rating-detail">
            Design Participants: {values.designParticipants || "Not provided"}
          </div>
          <div className="v3-rating-detail">
            {t.performer_label} Representative:{" "}
            {values.controlPerformerRepresentative || "Not provided"}
          </div>
          <div className="v3-rating-detail">
            Control ID: {values.controlId || "Not provided"}
          </div>
          {values.controlDescription && (
            <div
              className="v3-rating-detail"
              style={{ whiteSpace: "pre-wrap" }}
            >
              Control Description: {values.controlDescription}
            </div>
          )}
          {!t.req_hidden && values.regulationObligationIds && (
            <div
              className="v3-rating-detail"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {t.req_field_label} IDs: {values.regulationObligationIds}
            </div>
          )}
        </div>

        {/* Control Adequacy */}
        <div className="v3-rating-block">
          <div className="v3-rating-title">{t.section_cda_questions}</div>
          {/* <div className="v3-rating-detail">
            {controlYesCount}/18 questions answered Yes
          </div>
          {controlTotalAnswered < 18 && (
            <div className="v3-rating-detail" style={{ color: "#888" }}>
              ({controlTotalAnswered}/18 total answered)
            </div>
          )} */}
          <div
            className={`v3-rating-status ${controlAdequate ? "adequate" : "inadequate"}`}
          >
            Status:{" "}
            {controlAdequate
              ? t.rating_adequate.toUpperCase()
              : t.rating_inadequate.toUpperCase()}
          </div>
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{ width: `${Math.round((controlYesCount / 18) * 100)}%` }}
            />
          </div>
        </div>

        {/* Monitoring Effectiveness */}
        <div className="v3-rating-block">
          <div className="v3-rating-title">{t.section_mda_questions}</div>
          {/* <div className="v3-rating-detail">
            {monitoringYesCount}/9 applicable questions answered Yes
          </div>
          {monitoringTotalAnswered < 9 ? (
            <div className="v3-rating-detail" style={{ color: "#888" }}>
              ({monitoringTotalAnswered}/9 total answered)
            </div>
          ) : (
            <div className="v3-rating-detail" style={{ color: "#888" }}>
              All 9 monitoring questions answered
            </div>
          )} */}
          <div
            className={`v3-rating-status ${monitoringAdequate ? "adequate" : "inadequate"}`}
          >
            Status:{" "}
            {monitoringAdequate
              ? t.rating_adequate.toUpperCase()
              : t.rating_inadequate.toUpperCase()}
          </div>
          <div className="v3-progress-bar-wrap">
            <div
              className="v3-progress-bar"
              style={{
                width: `${Math.round((monitoringYesCount / 9) * 100)}%`,
              }}
            />
          </div>
        </div>

        {/* AI Rationale panel */}
        {aiRationale && (
          <AIPanel
            title="AI Assessment Rationale"
            confidence={aiRationale.confidence}
          >
            <p style={{ whiteSpace: "pre-wrap" }}>{aiRationale.rationale}</p>
          </AIPanel>
        )}

        {/* MDA Information */}
        <div className="v3-rating-block">
          <div className="v3-rating-title">{t.section_mda} (MDA)</div>
          {values.monitoringIssueId && (
            <div className="v3-rating-detail">
              {t.issue_label} ID: {values.monitoringIssueId}
            </div>
          )}
          <div className="v3-rating-detail">
            Monitoring Tool ID: {values.monitoringToolId || "Not provided"}
          </div>
          {values.monitoringToolDescription && (
            <div
              className="v3-rating-detail"
              style={{ whiteSpace: "pre-wrap" }}
            >
              Monitoring Tool Description: {values.monitoringToolDescription}
            </div>
          )}
        </div>

        {/* Team Members */}
        <div className="v3-rating-block">
          <div className="v3-rating-title">Team Members</div>
          <div className="v3-rating-detail">
            {values.teamMembers &&
            typeof values.teamMembers === "string" &&
            values.teamMembers.trim()
              ? values.teamMembers
              : "No team members added"}
          </div>
        </div>

        {/* Final Comment */}
        <div className="v3-form-group" style={{ marginTop: 16 }}>
          <Form.Item
            name="finalAssessmentComment"
            label={
              <span className="v3-form-label">Final Assessment Comment</span>
            }
            rules={[]}
          >
            <TextArea
              rows={4}
              placeholder="Provide your final assessment comment..."
            />
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    if (currentType === "CMA") {
      const stepKey = (steps[currentStep] as any)?.key;
      switch (stepKey) {
        case "basic":
          return renderBasicInfo();
        case "process":
          return renderProcessRiskInfo();
        case "control":
          return renderControlQuestions();
        case "monitoring":
          return renderMonitoringQuestions();
        case "review":
          return renderReview();
        default:
          return null;
      }
    }

    // CMDA flow
    switch (currentStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderCDA();
      case 2:
        return renderControlQuestions();
      case 3:
        return renderMDA();
      case 4:
        return renderMonitoringQuestions();
      case 5:
        return renderReview();
      default:
        return null;
    }
  };

  const next = async () => {
    if (currentStep >= steps.length - 1) return;

    try {
      await form.validateFields();
    } catch (err: any) {
      const firstError = err?.errorFields?.[0]?.errors?.[0];
      message.error(
        firstError || "Please fill in all required fields before continuing.",
      );
      if (err?.errorFields?.length) {
        form.scrollToField(err.errorFields[0].name, {
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    setAdvancing(true);
    // Auto-save progress as draft before advancing (silent; errors non-blocking)
    try {
      await handleSave("draft", { autoSave: true });
    } catch {
      // Non-blocking: advance even if auto-save failed
    }
    setAdvancing(false);

    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Auto-generate AI rationale when entering the final review step
    if (nextStep === steps.length - 1 && !aiRationale) {
      const values = form.getFieldsValue(true);
      if (currentType === "CMDA") {
        const ctrl = values.controlResponses || [];
        const mon = values.monitoringResponses || [];
        const ctlYes = ctrl.filter(
          (r: any) => r.questionId !== "C6" && r.response === "Y",
        ).length;
        const monYes = mon.filter(
          (r: any) =>
            r.questionId !== "M7" &&
            r.questionId !== "M8" &&
            r.response === "Y",
        ).length;
        const c6No =
          ctrl.find((r: any) => r.questionId === "C6")?.response === "N";
        const m7No =
          mon.find((r: any) => r.questionId === "M7")?.response === "N";
        const m8No =
          mon.find((r: any) => r.questionId === "M8")?.response === "N";
        generateRationale({
          type: "CMDA",
          controlAdequate: c6No && ctlYes >= 16,
          monitoringAdequate: m7No && m8No && monYes >= 7,
          score: { control: ctlYes, monitoring: monYes },
        })
          .then(setAiRationale)
          .catch(() => {});
      } else {
        generateRationale({
          type: "CMA",
          controlAdequate: true,
          monitoringAdequate: true,
        })
          .then(setAiRationale)
          .catch(() => {});
      }
    }
  };

  const prev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div>
      <div className="assessment-shell">
        {/* Back bar */}
        <div className="back-bar">
          <button
            className="back-btn"
            type="button"
            onClick={() => navigate("/assessments")}
          >
            &larr; Back to Assessments
          </button>
          <span className="assess-title">
            {isEdit ? "Edit Assessment" : "New Assessment"}
          </span>
        </div>

        {/* Custom Stepper */}
        {renderStepper()}

        {/* Step Content */}
        {pageLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 0",
              color: "var(--v3-gray-500, #888)",
            }}
          >
            <AIProcessing text="Loading assessment..." />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
          >
            <div className="v3-step-content">{renderStepContent()}</div>
          </Form>
        )}

        {/* Step Navigation */}
        {!pageLoading && (
          <div className="v3-step-nav">
            {currentStep > 0 && (
              <button
                className="v3-btn v3-btn-outline"
                type="button"
                onClick={prev}
                disabled={loading || advancing || saving}
              >
                Previous
              </button>
            )}
            {currentStep < steps.length - 1 && (
              <>
                <Button
                  className="v3-btn v3-btn-outline"
                  type="default"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await handleSave("draft", { silent: true });
                    } finally {
                      setSaving(false);
                    }
                  }}
                  loading={saving}
                  disabled={loading || advancing}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  className="v3-btn v3-btn-primary"
                  type="primary"
                  onClick={next}
                  loading={advancing}
                  disabled={loading || saving}
                >
                  {advancing ? "Saving..." : "Next"}
                </Button>
              </>
            )}
            {currentStep === steps.length - 1 && (
              <>
                <Button
                  className="v3-btn v3-btn-outline"
                  type="default"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await handleSave("draft");
                    } finally {
                      setSaving(false);
                    }
                  }}
                  loading={saving}
                  disabled={loading}
                >
                  {saving ? "Saving..." : "Save as Draft"}
                </Button>
                <Button
                  className="v3-btn v3-btn-primary"
                  type="primary"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await handleSave("submitted");
                    } finally {
                      setSaving(false);
                    }
                  }}
                  loading={saving}
                  disabled={loading}
                >
                  {saving ? "Submitting..." : "Submit Assessment"}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentForm;
